import React, { useState, useEffect, useCallback } from "react";
import "./decade_map.css";
import {
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Stack,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import MapIcon from "@mui/icons-material/Map";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useAuth } from "./authContext";
import { useProject } from "./ProjectContext";
import { useFilter } from "./FilterContext";
import TaxonomyPicker from "./TaxonomyPicker";
import {
  TAXONOMY_KIND,
  buildTaxonomyPayload,
  setThemeAtRank,
  clearThemeAtRank,
} from "./utils/taxonomy";
import FormsAPI from "./FormsAPI";
import { Close } from "@mui/icons-material";
import config from "./config";
import { apiCall } from "./api";
import { getIdToken } from "./firebase";
import ConfirmationDialog from "./ConfirmationDialog";
import { SHAPE_TYPE, VISIBILITY, DEFAULT_PROJECT_FORM_ID } from "./utils/constants";
import SubmissionUploadProgress from "./SubmissionUploadProgress";
import {
  buildEditFieldMaps,
  buildFieldDataPayload,
  getAdditionalFields,
  getCaptionField,
  getFileFields,
  getHeaderField,
  getPrimaryStoryTextField,
} from "./utils/formFieldBuckets";

// BUG-002 (caption edit): tiny per-file caption editor for existing media in
// the edit panel. Defined at module scope (not inside ContributionComp) so the
// component identity is stable across parent re-renders — otherwise the
// TextField would unmount/remount on every keystroke and lose focus.
//
// `initialCaption` seeds local state once; subsequent prop updates are ignored
// while the user is typing, so an in-flight optimistic update from the parent
// doesn't yank text mid-edit. We persist on blur (cheaper than per-keystroke
// network calls) and surface "Saving..." / "Saved" / "Save failed" inline.
function ExistingFileCaptionField({ fileId, initialCaption, onSave }) {
  const [value, setValue] = useState(initialCaption ?? "");
  const [status, setStatus] = useState("idle");
  const baselineRef = React.useRef(initialCaption ?? "");

  // If the parent's stored caption changes while the field is idle (e.g. after
  // a successful save echoes back), keep us in sync. Don't clobber an active
  // edit (status === "dirty") or an in-flight save.
  useEffect(() => {
    if (status === "idle" || status === "saved") {
      const next = initialCaption ?? "";
      setValue(next);
      baselineRef.current = next;
    }
  }, [initialCaption, status]);

  const handleBlur = async () => {
    const trimmed = value;
    if (trimmed === baselineRef.current) {
      setStatus("idle");
      return;
    }
    if (typeof onSave !== "function" || !fileId) return;
    setStatus("saving");
    const ok = await onSave(fileId, trimmed);
    if (ok) {
      baselineRef.current = trimmed;
      setStatus("saved");
      setTimeout(() => {
        setStatus((s) => (s === "saved" ? "idle" : s));
      }, 1500);
    } else {
      setStatus("error");
    }
  };

  const helperText =
    status === "saving"
      ? "Saving..."
      : status === "saved"
        ? "Saved"
        : status === "error"
          ? "Save failed — try again"
          : " ";

  return (
    <TextField
      size="small"
      fullWidth
      label="Caption (optional)"
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        if (status !== "dirty") setStatus("dirty");
      }}
      onBlur={handleBlur}
      helperText={helperText}
      error={status === "error"}
      sx={{
        mt: 0.75,
        "& .MuiInputLabel-root": { color: "var(--theme-color)" },
        "& .MuiFormHelperText-root": { minHeight: "1em", marginLeft: 0 },
        "& .MuiOutlinedInput-root": {
          "&.Mui-focused fieldset": { borderColor: "var(--theme-color)" },
        },
      }}
    />
  );
}

function ContributionComp(props) {
  const { user } = useAuth();
  const { project } = useProject();
  // Taxonomy catalogue (items 3 & 5). Same fetch the FilterBar/wizard use,
  // so the create + edit forms can show the chip picker with no extra round
  // trip. taxonomyIndex resolves an assigned id → its kind for edit prefill.
  const { taxonomies, taxonomyIndex } = useFilter();
  const isExhibitionMode = props.isExhibitionMode || false;
  // ISSUE-EDIT-PARITY: editMode reuses this entire panel for the legacy edit flow
  // so the create and edit experiences match exactly (minus the address/identity
  // sections, which don't apply when editing). When editMode is true, props
  // `existingItem`, `existingCarouselFiles`, `existingNonImageFiles`, plus the
  // edit-mode callbacks (onEditSave/onEditDelete/onEditCancel/onExistingFileDelete/
  // onFilesUploaded) drive the experience. The submit handler branches on this
  // flag and hits EDRDynamic.php instead of submitDynDataNU.php; new file uploads
  // post to uploadFiles.php eagerly (matching the prior EditStoryComponent
  // semantics) instead of being staged for batched submit.
  const editMode = Boolean(props.editMode);
  const existingItem = props.existingItem || null;
  const [contFirstName, setContFirstName] = useState("");
  const [contLastName, setContLastName] = useState("");
  const [contEmail, setContEmail] = useState("");
  const [forms, setForms] = useState([]);
  const [schemaStatus, setSchemaStatus] = useState(editMode ? "ready" : "loading");
  const schemaRequestRef = React.useRef(0);
  const legacyInitialFocusRef = React.useRef(null);
  const legacySchemaRetryRef = React.useRef(null);
  const legacyFormRef = React.useRef(null);
  const [restoreSchemaFocus, setRestoreSchemaFocus] = useState(false);
  const [selectedForm, setSelectedForm] = useState(() => {
    if (editMode && existingItem?.fields) {
      return { id: existingItem.form_id ?? null, fields: existingItem.fields };
    }
    return null;
  });
  const [formData, setFormData] = useState(() => {
    if (editMode && existingItem) {
      return {
        year: existingItem.year ?? props.year,
        address: "",
        loc_id: "",
        uid: user ? user.uid : "",
        form_id: existingItem.form_id ?? null,
        projectsid: project ? project.projectsid : null,
        field_data: (existingItem.fields || []).map((f) => ({
          field_id: f.field_id,
          field_value: f.field_value ?? "",
        })),
        visibility: existingItem.visibility,
      };
    }
    return {
      year: props.year,
      address: props.selectedAddress ?? "",
      loc_id: (() => {
        const st = props.selectionType || SHAPE_TYPE.MARKER;
        if (
          st === SHAPE_TYPE.POLYGON &&
          Array.isArray(props.polygonCoordinates) &&
          props.polygonCoordinates.length >= 3
        ) {
          return JSON.stringify(props.polygonCoordinates);
        }
        if (
          st === SHAPE_TYPE.LINE &&
          Array.isArray(props.lineCoordinates) &&
          props.lineCoordinates.length >= 2
        ) {
          return JSON.stringify(props.lineCoordinates);
        }
        if (props?.selectedAddressLatLng) {
          return `${parseFloat(props.selectedAddressLatLng.lat).toFixed(6)},${parseFloat(props.selectedAddressLatLng.lng).toFixed(6)}`;
        }
        return "";
      })(),
      uid: user ? user.uid : `${contFirstName}_${contLastName}_${contEmail}`,
      form_id: null,
      projectsid: project ? project.projectsid : null,
      field_data: [],
      visibility: props?.storyVisibility,
    };
  });
  const [error, setError] = useState({});
  const [files, setFiles] = useState({});
  const legacyFileInputRefs = React.useRef({});
  const selectionType = props.selectionType || SHAPE_TYPE.MARKER;
  const [progress, setProgress] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [confirmationTitle, setConfirmationTitle] = useState("");
  // ISSUE-004: stash the enriched submit response so we can hand the
  // (shape_type, shape_id) pair to the parent on confirmation close — same
  // contract the wizard uses, so MapComponent's handleSubmissionSuccess
  // doesn't have to special-case the legacy path.
  const lastSubmissionRef = React.useRef(null);
  const getDefaultVisibility = () => {
    if (editMode && existingItem?.visibility) {
      return existingItem.visibility;
    }
    if (props.addStoryMode && props.storyVisibility) {
      return props.storyVisibility;
    }
    const siteType = project?.site_type;
    if (siteType === 'exhibition') return VISIBILITY.PROJECT;
    if (siteType === 'city') return VISIBILITY.PUBLIC;
    return user ? VISIBILITY.PRIVATE : VISIBILITY.PROJECT;
  };
  const [visibility, setVisibility] = useState(getDefaultVisibility);

  // ── Taxonomy selection (items 3 & 5) ────────────────────────────────
  // Local, controlled state for the shared TaxonomyPicker. Create starts
  // empty; edit prefills from the existing assignment once the catalogue
  // has loaded (see effect below). Rank semantics use the same pure
  // helpers as the wizard reducer so behaviour can't diverge.
  const [themeRanks, setThemeRanks] = useState({ 1: null, 2: null, 3: null });
  // Client July 2026 (#7): Property Type + Communities are ranked like
  // Themes — each a fixed-shape `{1,2,3}` map of ids (or null).
  const [propertyTypeRanks, setPropertyTypeRanks] = useState({
    1: null,
    2: null,
    3: null,
  });
  const [communityRanks, setCommunityRanks] = useState({
    1: null,
    2: null,
    3: null,
  });
  // Wipe-safety flag (Codex eng-review): in editMode we only send taxonomy
  // keys to EDRDynamic once we've successfully derived the picker state from
  // the existing item AND the catalogue. If the catalogue never loads we
  // omit the keys entirely so EDRDynamic leaves assignments untouched rather
  // than wiping them with an empty set.
  const [taxonomyPrefilled, setTaxonomyPrefilled] = useState(false);
  const hasChipCatalogue = (taxonomies || []).length > 0;

  // Edit prefill: derive the {1,2,3} rank maps for every kind
  // ({themeRanks, propertyTypeRanks, communityRanks}) from the existing
  // item's taxonomy_ids + theme_ranks (the rank overlay the read endpoints
  // emit — keyed by ALL taxonomy ids, not just themes). Runs once, after
  // the catalogue is available so kinds resolve. This populated picker is
  // what prevents a save from wiping the shape's existing chips.
  useEffect(() => {
    if (!editMode || taxonomyPrefilled) return;
    if (!taxonomies || taxonomies.length === 0) return; // wait for catalogue
    const ids = Array.isArray(existingItem?.taxonomy_ids)
      ? existingItem.taxonomy_ids
      : [];
    const rankOverlay = existingItem?.theme_ranks || {};
    // Bucket the assigned ids by kind (client July 2026 #7: PT + community
    // can now hold up to three ranked ids each, just like themes).
    const themeIds = [];
    const propertyTypeIds = [];
    const communityIds = [];
    ids.forEach((rawId) => {
      const id = Number(rawId);
      if (!Number.isFinite(id) || id <= 0) return;
      const t = taxonomyIndex.get(id);
      if (!t) return;
      if (t.kind === TAXONOMY_KIND.THEME) themeIds.push(id);
      else if (t.kind === TAXONOMY_KIND.PROPERTY_TYPE) propertyTypeIds.push(id);
      else if (t.kind === TAXONOMY_KIND.COMMUNITY) communityIds.push(id);
    });
    // Order each kind's ids by their stored rank so Primary/Secondary/
    // Tertiary restore in order; unranked ids (rank fallback 1) keep input
    // order and fall to the next open slot so nothing is silently dropped.
    const ranksFromIds = (idList) => {
      const next = { 1: null, 2: null, 3: null };
      idList
        .map((id) => ({
          id,
          rank: Number(rankOverlay[id] ?? rankOverlay[String(id)]) || 1,
        }))
        .sort((a, b) => a.rank - b.rank)
        .forEach((entry, idx) => {
          const slot = idx + 1;
          if (slot <= 3) next[slot] = entry.id;
        });
      return next;
    };
    setThemeRanks(ranksFromIds(themeIds));
    setPropertyTypeRanks(ranksFromIds(propertyTypeIds));
    setCommunityRanks(ranksFromIds(communityIds));
    setTaxonomyPrefilled(true);
  }, [editMode, taxonomies, taxonomyIndex, existingItem, taxonomyPrefilled]);

  // Picker intent handlers. id === null clears the rank (compacts higher
  // ranks down via the shared helper). All three kinds are ranked (#7), so
  // they share the same kind-agnostic set/clear rank transforms.
  const handlePickThemeAtRank = useCallback((rank, id) => {
    setThemeRanks((prev) =>
      id == null ? clearThemeAtRank(prev, rank) : setThemeAtRank(prev, rank, id),
    );
  }, []);
  const handlePickPropertyTypeAtRank = useCallback((rank, id) => {
    setPropertyTypeRanks((prev) =>
      id == null ? clearThemeAtRank(prev, rank) : setThemeAtRank(prev, rank, id),
    );
  }, []);
  const handlePickCommunityAtRank = useCallback((rank, id) => {
    setCommunityRanks((prev) =>
      id == null ? clearThemeAtRank(prev, rank) : setThemeAtRank(prev, rank, id),
    );
  }, []);

  const [drawToolsOpen, setDrawToolsOpen] = useState(false);
  const isCustomSelect = Boolean(props.customSelectMode);
  const latLngLabel = props.selectedAddressLatLng
    ? `${props.selectedAddressLatLng.lat}, ${props.selectedAddressLatLng.lng}`
    : "";
  const projectMapLabel = project?.project_name
    ? `${project.project_name}`
    : "Project Map";

  useEffect(() => {
    if ((!user || isExhibitionMode) && visibility === VISIBILITY.PRIVATE) {
      const siteType = project?.site_type;
      setVisibility(siteType === 'city' ? VISIBILITY.PUBLIC : VISIBILITY.PROJECT);
    }
  }, [visibility, user, project?.site_type, isExhibitionMode]);

  // Default to marker

  // Fetch the public contribution schema. Edit mode synthesizes selectedForm
  // from the existing item and never depends on this read path.
  const loadContributionSchema = useCallback(async () => {
    if (editMode) {
      setSchemaStatus("ready");
      return;
    }
    const projectId = project?.projectsid;
    if (!projectId) {
      setSchemaStatus("error");
      return;
    }

    const requestId = ++schemaRequestRef.current;
    setSchemaStatus("loading");
    setForms([]);
    setSelectedForm(null);
    setFormData((previous) => ({
      ...previous,
      form_id: null,
      field_data: [],
      projectsid: projectId,
    }));

    try {
      const response = await FormsAPI.getFormsProject(projectId);
      if (requestId !== schemaRequestRef.current) return;
      const fetchedForms = Array.isArray(response?.data?.forms) ? response.data.forms : [];
      if (fetchedForms.length === 0) {
        setSchemaStatus("empty");
        return;
      }
      if (fetchedForms.some((form) => !Array.isArray(form?.fields))) {
        setSchemaStatus("error");
        return;
      }
      setForms(fetchedForms);
      setSchemaStatus("ready");
    } catch (_error) {
      if (requestId === schemaRequestRef.current) setSchemaStatus("error");
    }
  }, [editMode, project?.projectsid]);

  useEffect(() => {
    loadContributionSchema();
    return () => {
      schemaRequestRef.current += 1;
    };
  }, [loadContributionSchema]);

  useEffect(() => {
    if (!editMode) legacyInitialFocusRef.current?.focus({ preventScroll: true });
  }, [editMode]);

  useEffect(() => {
    if (!restoreSchemaFocus || schemaStatus === "loading") return;
    if (schemaStatus === "ready" && !selectedForm) return;
    setRestoreSchemaFocus(false);

    if (schemaStatus === "error" || schemaStatus === "empty") {
      legacySchemaRetryRef.current?.focus({ preventScroll: true });
      return;
    }

    legacyFormRef.current?.focus({ preventScroll: true });
  }, [schemaStatus, selectedForm, restoreSchemaFocus]);

  const retryContributionSchema = useCallback(() => {
    setRestoreSchemaFocus(true);
    loadContributionSchema();
  }, [loadContributionSchema]);

  // Update formData when selectedAddress changes
  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      address: props.selectedAddress ?? "",
    }));
  }, [props.selectedAddress]);

  // Keep loc_id in sync: polygon/line geometry wins over centroid lat/lng (Add A Story sets both).
  useEffect(() => {
    if (
      selectionType === SHAPE_TYPE.POLYGON &&
      Array.isArray(props.polygonCoordinates) &&
      props.polygonCoordinates.length >= 3
    ) {
      setFormData((prevData) => ({
        ...prevData,
        loc_id: JSON.stringify(props.polygonCoordinates),
      }));
      return;
    }
    if (
      selectionType === SHAPE_TYPE.LINE &&
      Array.isArray(props.lineCoordinates) &&
      props.lineCoordinates.length >= 2
    ) {
      setFormData((prevData) => ({
        ...prevData,
        loc_id: JSON.stringify(props.lineCoordinates),
      }));
      return;
    }
    if (props.selectedAddressLatLng) {
      setFormData((prevData) => ({
        ...prevData,
        loc_id: `${parseFloat(props.selectedAddressLatLng.lat).toFixed(6)},${parseFloat(props.selectedAddressLatLng.lng).toFixed(6)}`,
      }));
    }
  }, [selectionType, props.polygonCoordinates, props.lineCoordinates, props.selectedAddressLatLng]);

  const handleFormSelect = useCallback(
    (formId) => {
      const form = forms.find((f) => f.id === formId);

      if (!form) {
        console.error("Form not found for the given ID:", formId);
        return;
      }

      if (!Array.isArray(form.fields)) {
        setSchemaStatus("error");
        return;
      }

      setSelectedForm(form);

      setFormData((prevData) => ({
        ...prevData,
        form_id: form.id,
        field_data: form.fields.map((field) => ({
          field_id: field.field_id,
          field_value:
            field.field_type === "address" ? props.selectedAddress || "" : "",
        })),
      }));
    },
    [forms, props.selectedAddress],
  );
  useEffect(() => {
    if (editMode) return; // editMode synthesizes selectedForm from existingItem
    if (forms?.length > 0 && !selectedForm) {
      // Check if there is only one form, and select it by default if so
      if (forms?.length === 1) {
        handleFormSelect(forms[0].id);
      } else {
        // Attempt to select the form with the given ID
        const fallback =
          forms.find((f) => f.id === DEFAULT_PROJECT_FORM_ID) || forms[0];
        handleFormSelect(fallback.id);
      }
    }
  }, [forms, selectedForm, handleFormSelect, editMode]);

  const handleChange = async (event) => {
    const { name, value } = event.target;

    if (name === "address") {
      props.setSelectedAddress(value);
      setFormData((prevData) => ({ ...prevData, address: value }));

      // If the address field is updated, query the Google API to get lat, lng
      if (value) {
        try {
          const encodedAddress = encodeURIComponent(value);
          const apiKey = config.GOOGLE_MAPS_API_KEY;
          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${apiKey}`,
          );
          const data = await response.json();

          if (data.status === "OK") {
            let { lat, lng } = data.results[0].geometry.location;

            if (props.locations && props.locations.length > 0) {
              // First: try to match by address text against existing locations
              const typedAddress = value.trim().toLowerCase();
              const googleAddress = data.results[0].formatted_address?.trim().toLowerCase() || "";
              const addressMatch = props.locations.find(
                (loc) =>
                  loc.address &&
                  loc.loc_lat != null &&
                  loc.loc_lng != null &&
                  (loc.address.trim().toLowerCase() === typedAddress ||
                    loc.address.trim().toLowerCase() === googleAddress),
              );

              if (addressMatch) {
                lat = addressMatch.loc_lat;
                lng = addressMatch.loc_lng;
              } else {
                // Fallback: snap to closest existing marker within 50ft
                const nearThresholdKm = 0.01524;
                let closestDist = Infinity;
                props.locations.forEach((loc) => {
                  if (loc.loc_lat != null && loc.loc_lng != null) {
                    const dLat = (loc.loc_lat - lat) * (Math.PI / 180);
                    const dLng = (loc.loc_lng - lng) * (Math.PI / 180);
                    const a =
                      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                      Math.cos(lat * (Math.PI / 180)) *
                      Math.cos(loc.loc_lat * (Math.PI / 180)) *
                      Math.sin(dLng / 2) * Math.sin(dLng / 2);
                    const d = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                    if (d < closestDist && d <= nearThresholdKm) {
                      closestDist = d;
                      lat = loc.loc_lat;
                      lng = loc.loc_lng;
                    }
                  }
                });
              }
            }

            setFormData((prevData) => ({
              ...prevData,
              loc_id: `${parseFloat(lat).toFixed(6)},${parseFloat(lng).toFixed(6)}`,
            }));
            props.setSelectedAddressLatLng?.({ lat, lng });
          } else {
            console.error("Error fetching geolocation:", data.status);
          }
        } catch (error) {
          console.error("Error during geolocation fetch:", error);
        }
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }

    setError((prev) => ({
      ...prev,
      [name]: value ? "" : `${name} is required`,
    }));
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData((prevData) => {
      const updatedFieldData = prevData.field_data.map((field) =>
        field.field_id === fieldId ? { ...field, field_value: value } : field,
      );

      // If the field is an address field, also update the props.selectedAddress
      const addressField = selectedForm?.fields?.find(
        (field) => field.field_id === fieldId && field.field_type === "address",
      );
      if (addressField) {
        props.setSelectedAddress(value);
      }

      return { ...prevData, field_data: updatedFieldData };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      (!editMode && schemaStatus !== "ready")
      || !selectedForm
      || !Array.isArray(selectedForm.fields)
    ) {
      setError({ schema: "The contribution form is not ready. Please retry loading it." });
      return;
    }

    // Validate required fields
    let newErrors = {};
    if (!formData.year) newErrors.year = "Year is required";
    if (
      !editMode &&
      selectionType === SHAPE_TYPE.MARKER &&
      !formData.address &&
      !props.allowEmptyAddress
    ) {
      newErrors.address = "Address is required";
    }
    if (!editMode && selectionType === SHAPE_TYPE.POLYGON && !formData.loc_id) {
      newErrors.region = "A region is required.";
    }
    if (!editMode && selectionType === SHAPE_TYPE.LINE && !formData.loc_id) {
      newErrors.region = "A line is required.";
    }
    if (!editMode && (!user || isExhibitionMode)) {
      if (!contFirstName.trim()) {
        newErrors.contFirstName = "First name is required";
      }
      if (!contLastName.trim()) {
        newErrors.contLastName = "Last name is required";
      }
      if (!contEmail.trim()) {
        newErrors.contEmail = "Email is required";
      }
    }

    const headerField = formData.field_data.find((field) => {
      const formField = selectedForm.fields.find(
        (f) => f.field_id === field.field_id,
      );
      return formField && formField.field_type === "header";
    });

    if (!headerField || !String(headerField.field_value || "").trim()) {
      newErrors.header = "A header is required for submission.";
    }

    if (Object.keys(newErrors)?.length > 0) {
      setError(newErrors);
      return;
    }

    // editMode: persist via EDRDynamic.php through the parent's onEditSave callback.
    // We send the contribution values keyed BOTH ways: `fields_by_id`
    // (authoritative, collision-proof) and `fields` (legacy field_name map,
    // kept for backward compatibility with a server that predates the
    // field_id edit path). field_ids are unique, so fields_by_id is immune
    // to the duplicate-field-name collision that erased "Place Name" on the
    // MKE map (June-25 investigation); EDRDynamic prefers it.
    if (editMode) {
      const { fieldsById, fieldsByName } = buildEditFieldMaps(
        selectedForm?.fields,
        formData.field_data,
      );
      const editPayload = {
        year: formData.year,
        visibility,
        fields: fieldsByName,
        fields_by_id: fieldsById,
      };
      // Item 3: include taxonomy only when we've safely prefilled the picker
      // from the existing item (catalogue loaded + state derived). EDRDynamic
      // treats a present `taxonomy_ids` key as authoritative — including an
      // empty array would WIPE the shape's chips — so when the catalogue
      // never loaded we omit the keys and the backend leaves assignments
      // untouched (wipe-safety invariant).
      if (taxonomyPrefilled) {
        const { taxonomyIds: editTaxonomyIds, taxonomyRanks: editTaxonomyRanks } =
          buildTaxonomyPayload({ themeRanks, propertyTypeRanks, communityRanks });
        editPayload.taxonomy_ids = editTaxonomyIds;
        editPayload.taxonomy_ranks = editTaxonomyRanks;
      }
      try {
        const ok = await props.onEditSave?.(editPayload);
        if (ok) {
          if (visibility !== VISIBILITY.PRIVATE) {
            const visibilityLabel =
              visibility === VISIBILITY.PROJECT ? projectMapLabel : "Public";
            const message =
              project?.site_type === "exhibition"
                ? "Thanks for your contribution. Your content will be reviewed and if approved will be posted within the next 48 hours."
                : `Thanks for your contribution. Your content will be reviewed soon for ${visibilityLabel} display approval.`;
            setConfirmationTitle("Submission received");
            setConfirmationMessage(message);
            setConfirmationOpen(true);
          } else {
            // Private edit: skip the confirmation and just close the editor.
            props.onEditCancel?.();
          }
        }
      } catch (err) {
        console.error("Edit save failed:", err);
        alert("An error occurred while saving the changes.");
      }
      return;
    }

    try {
      const submissionFormData = new FormData();
      submissionFormData.append("uid", formData.uid);
      submissionFormData.append("year", formData.year);
      submissionFormData.append("form_id", formData.form_id);
      submissionFormData.append("projectsid", formData.projectsid);
      submissionFormData.append("shape_type", selectionType);
      submissionFormData.append("loc_id", formData.loc_id);

      // Use lat/lng as address for custom select mode
      const addressValue = isCustomSelect
        ? latLngLabel
        : props.selectedAddress || "";

      const formFields = selectedForm.fields;
      const byId = Object.fromEntries(
        formData.field_data.map((f) => [f.field_id, f.field_value ?? ""]),
      );
      const headerFieldDef = getHeaderField(formFields);
      const captionFieldDef = getCaptionField(formFields);
      const storyFieldDef = getPrimaryStoryTextField(formFields);
      const additionalFieldList = getAdditionalFields(formFields);
      const placeName = headerFieldDef ? (byId[headerFieldDef.field_id] ?? "") : "";
      const captionVal = captionFieldDef ? (byId[captionFieldDef.field_id] ?? "") : "";
      const storyText = storyFieldDef ? (byId[storyFieldDef.field_id] ?? "") : "";
      const additionalValues = {};
      additionalFieldList.forEach((f) => {
        additionalValues[f.field_id] = byId[f.field_id] ?? "";
      });
      const syncedFieldData = buildFieldDataPayload(formFields, {
        address: addressValue,
        placeName,
        caption: captionVal,
        storyText,
        additionalValues,
      });

      submissionFormData.append("field_data", JSON.stringify(syncedFieldData));
      // Use the selected visibility directly - all users can submit for moderation review
      const finalVisibility = visibility;
      submissionFormData.append("visibility", finalVisibility);

      // Item 5: taxonomy chips picked in the add-place form. Shared
      // serializer = byte-identical to the wizard's payload. Backend
      // (submitDynDataNU.php) parses taxonomy_ids[] + taxonomy_ranks and
      // routes by shape_type, so markers + polygons + lines all persist.
      const { taxonomyIds: createTaxonomyIds, taxonomyRanks: createTaxonomyRanks } =
        buildTaxonomyPayload({ themeRanks, propertyTypeRanks, communityRanks });
      createTaxonomyIds.forEach((id) => {
        submissionFormData.append("taxonomy_ids[]", String(id));
      });
      if (createTaxonomyRanks.length > 0) {
        submissionFormData.append(
          "taxonomy_ranks",
          JSON.stringify(createTaxonomyRanks),
        );
      }

      // Add files + per-file captions (same order as submitDynDataNU.php expects)
      for (const fieldId in files) {
        (files[fieldId] || []).forEach((entry) => {
          const blob = entry.file != null ? entry.file : entry;
          submissionFormData.append(`file_${fieldId}[]`, blob);
          submissionFormData.append(
            `caption_${fieldId}[]`,
            entry.caption != null ? entry.caption : "",
          );
        });
      }

      // Use XMLHttpRequest for upload progress tracking. apiCall() doesn't
      // expose upload-progress events, so we keep XHR here and attach the
      // Authorization header by hand. Auth refactor 2026-05-27: the server
      // prefers the verified token over $_POST['uid']; we still ship the
      // synthetic uid field so anonymous (no-token) exhibition submissions
      // continue to work via submitDynDataNU.php's optional_uid fallback.
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${config.getApiUrl("submitDynDataNU.php")}`);
      try {
        const token = await getIdToken();
        if (token) {
          xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        }
      } catch (_err) {
        // No token (signed out, or token fetch failed). The server will
        // fall back to optional_uid + $_POST['uid'] for the anonymous path.
      }

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round(
            (event.loaded / event.total) * 100,
          );
          setProgress(percentCompleted);
        }
      };

      xhr.onload = () => {
        const result = JSON.parse(xhr.responseText);
        if (result.success) {
          // ISSUE-004: capture the new shape's identity so confirmation
          // close can pop the side panel onto it.
          lastSubmissionRef.current = {
            shapeType: result?.shape_type || selectionType || 'marker',
            shapeId: result?.shape_id ?? null,
          };
          let message;
          if (finalVisibility === VISIBILITY.PRIVATE) {
            message = "Thanks for your contribution.";
          } else if (project?.site_type === 'exhibition') {
            message = "Thanks for your contribution. Your content will be reviewed and if approved will be posted within the next 48 hours.";
          } else {
            const visibilityLabel =
              finalVisibility === VISIBILITY.PROJECT
                ? projectMapLabel
                : "Public";
            message = `Thanks for your contribution. Your content will be reviewed soon for ${visibilityLabel} display approval.`;
          }
          setConfirmationTitle("Submission received");
          setConfirmationMessage(message);
          setConfirmationOpen(true);
        } else {
          alert(`Error: ${result.message}`);
        }
        setProgress(0); // Reset progress
      };

      xhr.onerror = () => {
        alert("An error occurred during submission.");
        setProgress(0); // Reset progress
      };

      xhr.send(submissionFormData);
    } catch (error) {
      console.error("Error during submission:", error);
      alert("Submission failed.");
      setProgress(0); // Reset progress
    }
  };

  // editMode: eagerly upload picked files to uploadFiles.php (parity with the
  // prior EditStoryComponent behavior). The contribution already exists, so we
  // attach to its id (`existingItem.id`) and let the parent refetch via
  // `onFilesUploaded`. Captions for new uploads aren't surfaced in this UI yet
  // — keeping scope tight; existing files render with their stored captions.
  const handleEagerFileUpload = async (fieldId, fileList) => {
    if (!editMode || !existingItem?.id || !user || !fileList?.length) return;
    const formDataPayload = new FormData();
    if (existingItem.shape_type === SHAPE_TYPE.POLYGON) {
      formDataPayload.append("polygon_id", existingItem.id);
    } else if (existingItem.shape_type === SHAPE_TYPE.LINE) {
      formDataPayload.append("polyline_id", existingItem.id);
    } else {
      formDataPayload.append("submission_id", existingItem.id);
    }
    formDataPayload.append("field_id", fieldId);
    // Auth refactor 2026-05-27: kept firebase_uid in the body for the
    // anonymous-fallback path on uploadFiles.php; the server prefers the
    // verified ID token (apiCall attaches it) and only uses the body field
    // when no token is present.
    formDataPayload.append("firebase_uid", user.uid);
    fileList.forEach((file) => {
      formDataPayload.append("files[]", file);
    });
    try {
      const resp = await apiCall("uploadFiles.php", {
        method: "POST",
        body: formDataPayload,
      });
      if (resp?.success) {
        props.onFilesUploaded?.();
      } else {
        alert(`Error uploading files: ${resp?.message ?? "unknown"}`);
      }
    } catch (err) {
      console.error("Error uploading files:", err);
      alert("An error occurred while uploading files.");
    }
  };

  const handleFileChange = (event, fieldId) => {
    const selectedFiles = Array.from(event.target.files);
    if (editMode) {
      // Reset the input so the same file can be re-picked if needed.
      event.target.value = "";
      handleEagerFileUpload(fieldId, selectedFiles);
      return;
    }
    const wrapped = selectedFiles.map((file) => ({ file, caption: "" }));

    setFiles((prev) => ({
      ...prev,
      [fieldId]: [...(prev[fieldId] || []), ...wrapped],
    }));
  };

  const handleFileCaptionChange = (fieldId, index, caption) => {
    setFiles((prev) => {
      const list = prev[fieldId] || [];
      if (!list[index]) return prev;
      const next = list.map((item, i) => {
        if (i !== index) return item;
        const file = item.file != null ? item.file : item;
        return { file, caption };
      });
      return { ...prev, [fieldId]: next };
    });
  };

  const handleRemoveFile = (fieldId, fileName) => {
    setFiles((prev) => {
      // Safeguard: Check if fieldId exists in the files object
      if (!prev[fieldId]) {
        console.warn(`No files found for fieldId: ${fieldId}`);
        return prev;
      }

      return {
        ...prev,
        [fieldId]: prev[fieldId].filter((entry) => {
          const f = entry.file != null ? entry.file : entry;
          return f.name !== fileName;
        }),
      };
    });
  };

  const handleYearChange = (event) => {
    if (editMode) {
      setFormData((prevData) => ({ ...prevData, year: event.target.value }));
      return;
    }
    props.setYear(event.target.value); // Update the shared year state
  };
  useEffect(() => {
    if (editMode) return; // editMode owns year locally so it doesn't get clobbered
    setFormData((prevData) => ({
      ...prevData,
      year: props.year,
    }));
  }, [props.year, editMode]);
  useEffect(() => {
    if (!user || isExhibitionMode) {
      setFormData((prevData) => ({
        ...prevData,
        uid: `${contFirstName}_${contLastName}_${contEmail}`,
      }));
    }
  }, [user, contFirstName, contLastName, contEmail, isExhibitionMode]);

  const handleConfirmationClose = () => {
    setConfirmationOpen(false);
    if (editMode) {
      // editMode: just close the editor — parent owns the side-panel state.
      props.onEditCancel?.();
      return;
    }
    props.setShowContribution(false);
    props.setAddStoryMode(false);
    props.clearSelectedAddress();
    props.refreshData();
    if (selectionType === SHAPE_TYPE.POLYGON) {
      props.onPolygonSubmitted?.();
    }
    if (selectionType === SHAPE_TYPE.LINE) {
      props.onLineSubmitted?.();
    }
    // ISSUE-004: hand the new shape over to the parent so the side panel can
    // open pre-focused on it. Mirrors the wizard's hand-off contract.
    const submission = lastSubmissionRef.current;
    if (submission?.shapeId != null && props.onSubmissionSuccess) {
      props.onSubmissionSuccess(submission);
    }
    lastSubmissionRef.current = null;
  };

  return (
    <>
      <div
        role="dialog"
        aria-modal="false"
        aria-label={editMode ? "Edit contribution form" : "Add a place form"}
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
          background: "var(--theme-bg-header)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        {editMode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              px: 4,
              paddingTop: "10px",
              paddingBottom: "5px",
              flexShrink: 0,
              boxSizing: "border-box",
            }}
          >
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={async () => {
                if (window.confirm("Are you sure you want to delete this entry?")) {
                  await props.onEditDelete?.();
                }
              }}
              sx={{
                background: "var(--theme-btn-clr)",
                color: "var(--theme-danger-clr)",
                textTransform: "capitalize !important",
                fontWeight: "400 !important",
                padding: "6px 10px",
                border: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "var(--theme-btn-clr) !important",
                  boxShadow: "none",
                },
              }}
            >
              Delete
            </Button>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Button
                onClick={() => props.onEditCancel?.()}
                startIcon={<CancelOutlinedIcon />}
                sx={{
                  background: "var(--theme-btn-clr)",
                  color: "var(--theme-danger-clr)",
                  textTransform: "capitalize !important",
                  fontWeight: "400 !important",
                  padding: "6px 10px",
                  border: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "var(--theme-btn-clr) !important",
                    boxShadow: "none",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={(e) => handleSubmit(e)}
                variant="contained"
                disabled={!selectedForm || !Array.isArray(selectedForm.fields)}
                startIcon={<CheckCircleOutlinedIcon />}
                sx={{
                  background: "var(--theme-btn-clr)",
                  color: "var(--theme-success-clr) !important",
                  fontWeight: "400 !important",
                  textTransform: "capitalize !important",
                  padding: "6px 10px",
                  border: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "var(--theme-btn-clr) !important",
                    boxShadow: "none",
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
        {/* Draw Tools tab hanging outside the panel to the left.
            Hidden in editMode — placement is fixed for an existing record. */}
        {!props.addStoryMode && !editMode && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: "120px",
              transform: "translateX(-100%)",
              zIndex: 1001,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <Box
              onClick={() => setDrawToolsOpen((prev) => !prev)}
              sx={{
                writingMode: "vertical-rl",
                textOrientation: "mixed",
                transform: "rotate(180deg)",
                cursor: "pointer",
                padding: "12px 6px",
                backgroundColor: "var(--theme-bg-header)",
                color: "var(--theme-color)",
                fontWeight: 700,
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                borderRadius: "6px 6px 0 0",
                userSelect: "none",
                border: "none",
                outline: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "var(--theme-bg-clr)",
                },
              }}
            >
              Draw Tools
            </Box>
            {drawToolsOpen && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  padding: "6px",
                  backgroundColor: "rgba(245,245,245,0.95)",
                  borderRadius: "6px 0 0 6px",
                  boxShadow: "-2px 2px 6px rgba(0,0,0,0.15)",
                }}
              >
                <Tooltip title="Point" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, loc_id: "" }));
                      props.onPolygonSubmitted?.();
                    }}
                    sx={{
                      color: "var(--theme-color)",
                      backgroundColor: selectionType === SHAPE_TYPE.MARKER ? "var(--theme-bg-clr)" : "#fff",
                      border: selectionType === SHAPE_TYPE.MARKER ? "2px solid var(--theme-color)" : "1px solid transparent",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "var(--theme-bg-clr)" },
                    }}
                  >
                    <LocationOnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Polygon" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      props.activatePolygonDrawing?.();
                    }}
                    sx={{
                      color: "var(--theme-color)",
                      backgroundColor: selectionType === SHAPE_TYPE.POLYGON ? "var(--theme-bg-clr)" : "#fff",
                      border: selectionType === SHAPE_TYPE.POLYGON ? "2px solid var(--theme-color)" : "1px solid transparent",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "var(--theme-bg-clr)" },
                    }}
                  >
                    <PolylineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Line" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => {
                      props.activateLineDrawing?.();
                    }}
                    sx={{
                      color: "var(--theme-color)",
                      backgroundColor: selectionType === SHAPE_TYPE.LINE ? "var(--theme-bg-clr)" : "#fff",
                      border: selectionType === SHAPE_TYPE.LINE ? "2px solid var(--theme-color)" : "1px solid transparent",
                      borderRadius: "6px",
                      "&:hover": { backgroundColor: "var(--theme-bg-clr)" },
                    }}
                  >
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}
        <div
          style={{
            width: "100%",
            flexGrow: 1,
            overflowY: "scroll",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            position: "relative",
          }}
        >
        {!editMode && (
          <Box
            sx={{ position: "absolute", top: "-12px", right: 0, zIndex: 10000 }}
          >
            <IconButton
              ref={legacyInitialFocusRef}
              aria-label="Close add place form"
              onClick={() => {
                props.setShowContribution(false);
                props.setSelectedAddress?.("");
                setFormData((prevData) => ({ ...prevData, address: "" }));
                props.clearSelectedAddress();
                props.setSelectedMarker(null);
                props.setAddStoryMode(false);
              }}
            >
              <Close />
            </IconButton>
          </Box>
        )}
        <form
          ref={legacyFormRef}
          tabIndex={-1}
          aria-label="Contribution details"
          onSubmit={handleSubmit}
          style={{
            width: "90%",
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
            alignContent: "center",
            alignItems: "stretch",
            justifyContent: "flex-start",
          }}
        >
          {!editMode && schemaStatus === "loading" && (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid var(--ws-border-default, #DDDAD4)",
                borderRadius: "var(--ws-radius-sm, 4px)",
                background: "var(--ws-surface-well, #FAF8F2)",
                color: "var(--ws-color-body, #413A36)",
              }}
            >
              Loading contribution form…
            </Box>
          )}
          {!editMode && (schemaStatus === "error" || schemaStatus === "empty") && (
            <Box
              role="alert"
              sx={{
                mt: 2,
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                border: "1px solid var(--ws-status-danger, #C34700)",
                borderRadius: "var(--ws-radius-sm, 4px)",
                background: "var(--ws-status-danger-bg, #FBE9E7)",
                color: "var(--ws-color-body, #413A36)",
              }}
            >
              <span>
                {schemaStatus === "empty"
                  ? "No contribution form is configured for this project."
                  : "Couldn’t load the contribution form."}
              </span>
              <Button
                ref={legacySchemaRetryRef}
                type="button"
                onClick={retryContributionSchema}
                sx={{ minHeight: 44, color: "var(--ws-color-ink, #1F1F1F)" }}
              >
                Retry
              </Button>
            </Box>
          )}
          {!editMode && forms?.length > 1 && (
            <FormControl margin="normal" sx={{ marginTop: 4, marginBottom: 4 }}>
              <InputLabel id="contribution-form-select-label">Select a Form</InputLabel>
              <Select
                id="contribution-form-select"
                labelId="contribution-form-select-label"
                value={selectedForm ? selectedForm.id : ""}
                onChange={(e) => handleFormSelect(e.target.value)}
                label="Select a Form"
              >
                {forms.map((form) => (
                  <MenuItem key={form.id} value={form.id}>
                    {form.form_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!editMode && props.addStoryMode && selectionType === SHAPE_TYPE.MARKER && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Typography
                sx={{
                  color: "var(--theme-color)",
                  fontWeight: "700",
                  textTransform: "capitalize",
                }}
              >
                Location
              </Typography>
              <TextField
                label="Address"
                type="text"
                name="address"
                value={props.selectedAddress || ""}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    color: "var(--theme-color)",
                    backgroundColor: "rgba(0,0,0,0.04)",
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
            </Box>
          )}
          {!editMode && !props.addStoryMode && selectionType === SHAPE_TYPE.MARKER && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <Typography
                sx={{
                  color: "var(--theme-color)",
                  fontWeight: "700",
                  textTransform: "capitalize",
                }}
              >
                Select Place By Address
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    flexBasis: 0,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label={isCustomSelect ? "Lat/Long" : "Type Address"}
                    type="text"
                    name="address"
                    value={isCustomSelect ? latLngLabel : formData.address ?? ""}
                    onChange={handleChange}
                    error={Boolean(error.address)}
                    helperText={error.address}
                    inputProps={isCustomSelect ? { readOnly: true } : undefined}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    sx={{
                      "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "var(--theme-color)",
                      },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "var(--theme-color)",
                        },
                      },
                    }}
                  />
                  <Button
                    variant="text"
                    onClick={() => {
                      props.setCustomSelectMode?.(true);
                      props.setSelectedAddress?.(null);
                      setFormData((prevData) => ({ ...prevData, address: "" }));
                      props.setSelectMode(true);
                    }}
                    sx={{
                      justifyContent: "flex-start",
                      color: "var(--theme-color)",
                      textTransform: "none",
                      fontWeight: isCustomSelect ? 800 : 600,
                      padding: "4px 0 0 0",
                      minWidth: "unset",
                      textDecoration: "underline",
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Custom Select on Map By Lat/Long
                  </Button>
                </Box>
                <Typography sx={{ alignSelf: "flex-start", marginTop: "18px" }}>
                  <strong>OR</strong>
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    props.setCustomSelectMode?.(false);
                    props.setSelectMode(true);
                  }}
                  sx={{
                    color: isCustomSelect
                      ? "rgba(65, 58, 54, 0.45)"
                      : "var(--theme-color)",
                    fontWeight: "900 !important",
                    textTransform: "capitalize !important",
                    lineHeight: "unset",
                    flexGrow: 1,
                    flexBasis: 0,
                    minWidth: 0,
                    alignSelf: "flex-start",
                    border: isCustomSelect
                      ? "1px solid rgba(65, 58, 54, 0.3)"
                      : "1px solid var(--theme-color)",
                    paddingBlock: "16.5px",
                    "&:hover": {
                      background: "rgb(96 58 54 / 11%)",
                      border: "1px solid var(--theme-color)",
                    },
                  }}
                >
                  Click On Map
                </Button>
              </Box>
            </Box>
          )}
          {!editMode && !props.addStoryMode && selectionType === SHAPE_TYPE.POLYGON && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Typography
                sx={{
                  color: "var(--theme-color)",
                  fontWeight: "700",
                  textTransform: "capitalize",
                }}
              >
                Draw a Region
              </Typography>
              <Typography sx={{ color: "var(--theme-color)" }}>
                Use the polygon tool on the map to draw a loop and finish the
                region.
              </Typography>
              {error.region && (
                <Typography sx={{ color: "var(--theme-danger-clr)" }}>
                  {error.region}
                </Typography>
              )}
            </Box>
          )}
          {!editMode && !props.addStoryMode && selectionType === SHAPE_TYPE.LINE && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <Typography
                sx={{
                  color: "var(--theme-color)",
                  fontWeight: "700",
                  textTransform: "capitalize",
                }}
              >
                Draw a Line
              </Typography>
              <Typography sx={{ color: "var(--theme-color)" }}>
                Use the line tool on the map to drop points along a path.
                Double-click to finish.
              </Typography>
              {error.region && (
                <Typography sx={{ color: "var(--theme-danger-clr)" }}>
                  {error.region}
                </Typography>
              )}
            </Box>
          )}
          <FormControl
            sx={{
              marginTop: "20px",
              marginBottom: "10px",
              "& .MuiInputLabel-root": { color: "var(--theme-color)" },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--theme-color)",
              },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "var(--theme-color)",
                },
              },
            }}
          >
            <InputLabel id="contribution-year-label">Year</InputLabel>
            <Select
              id="contribution-year"
              labelId="contribution-year-label"
              label="Year"
              name="year"
              value={editMode ? (formData.year || "") : props.year}
              onChange={handleYearChange}
              error={Boolean(error.year)}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="1760">Pre-1770</MenuItem>
              {[...Array(26).keys()].map((i) => (
                <MenuItem key={i} value={1770 + i * 10}>
                  {1770 + i * 10}s
                </MenuItem>
              ))}
              <MenuItem value="2030">Vision</MenuItem>
            </Select>
          </FormControl>

          <FormControl
            sx={{
              marginTop: "10px",
              marginBottom: "10px",
              "& .MuiInputLabel-root": { color: "var(--theme-color)" },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "var(--theme-color)",
              },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "var(--theme-color)",
                },
              },
            }}
          >
            <InputLabel id="contribution-visibility-label">Visibility</InputLabel>
            <Select
              id="contribution-visibility"
              labelId="contribution-visibility-label"
              label="Visibility"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              disabled={!project}
            >
              {/* Exhibition: Private (disabled in exhibition mode) + Project */}
              {/* Default/Classroom: Private (disabled if !user) + Project + Public */}
              {/* City + logged in: Private + Public */}
              {/* City + not logged in: Public only */}

              {/* Private: show for non-city (disabled if !user or exhibition mode), show for city only if logged in */}
              {project?.site_type === 'city' ? (
                user && !isExhibitionMode && (
                  <MenuItem value={VISIBILITY.PRIVATE}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LockIcon sx={{ marginRight: 1 }} />
                      Private
                    </Box>
                  </MenuItem>
                )
              ) : (
                <MenuItem value={VISIBILITY.PRIVATE} disabled={!user || isExhibitionMode}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LockIcon sx={{ marginRight: 1 }} />
                    Private
                  </Box>
                </MenuItem>
              )}

              {/* Project: show for non-city only (exhibition + default) */}
              {project?.site_type !== 'city' && (
                <MenuItem value={VISIBILITY.PROJECT}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <MapIcon sx={{ marginRight: 1 }} />
                    {projectMapLabel}
                  </Box>
                </MenuItem>
              )}

              {/* Public: show for non-exhibition only (city + default) */}
              {project?.site_type !== 'exhibition' && (
                <MenuItem value={VISIBILITY.PUBLIC}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PublicIcon sx={{ marginRight: 1 }} />
                    Public
                  </Box>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {selectedForm &&
            (() => {
              const formFields = selectedForm.fields;
              const headerField = getHeaderField(formFields);
              const captionField = getCaptionField(formFields);
              const primaryStory = getPrimaryStoryTextField(formFields);
              const fileFieldsList = getFileFields(formFields);
              const additionalFieldsList = getAdditionalFields(formFields);

              const fieldSx = {
                marginTop: "10px",
                marginBottom: "10px",
                "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "var(--theme-color)",
                },
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--theme-color)",
                  },
                },
              };

              const readValue = (fieldId) =>
                formData.field_data.find((f) => f.field_id === fieldId)
                  ?.field_value || "";

              const getFileIcon = (filepath) => {
                if (!filepath) return "./file.png";
                const ext = filepath.toLowerCase().split(".").pop();
                const videoExts = [
                  "mp4",
                  "avi",
                  "mov",
                  "wmv",
                  "flv",
                  "webm",
                  "mkv",
                  "m4v",
                ];
                const audioExts = [
                  "mp3",
                  "wav",
                  "aac",
                  "flac",
                  "ogg",
                  "wma",
                ];
                return videoExts.includes(ext) || audioExts.includes(ext)
                  ? "./video.png"
                  : "./file.png";
              };

              return (
                <>
                  {headerField && (
                    <TextField
                      key={`header-${headerField.field_id}`}
                      fullWidth
                      sx={fieldSx}
                      label={headerField.field_name}
                      value={readValue(headerField.field_id)}
                      onChange={(e) =>
                        handleFieldChange(headerField.field_id, e.target.value)
                      }
                      error={Boolean(error.header)}
                      helperText={
                        error.header
                          ? `${headerField.field_name} is required`
                          : ""
                      }
                    />
                  )}
                  {captionField && (
                    <TextField
                      key={`caption-${captionField.field_id}`}
                      fullWidth
                      sx={fieldSx}
                      label={captionField.field_name}
                      value={readValue(captionField.field_id)}
                      onChange={(e) =>
                        handleFieldChange(captionField.field_id, e.target.value)
                      }
                    />
                  )}
                  {primaryStory && (
                    <TextField
                      key={`story-${primaryStory.field_id}`}
                      multiline
                      fullWidth
                      minRows={4}
                      maxRows={20}
                      sx={fieldSx}
                      label={primaryStory.field_name}
                      value={readValue(primaryStory.field_id)}
                      onChange={(e) =>
                        handleFieldChange(primaryStory.field_id, e.target.value)
                      }
                    />
                  )}
                  {/* Taxonomy picker (items 3 & 5): tag the place with
                      themes / property type / communities on both create
                      and edit, for markers + polygons + lines. Sits between
                      the story and the file upload, matching the wizard's
                      step ordering. Hidden when the project seeds no chips. */}
                  {hasChipCatalogue && (
                    <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
                      <Typography
                        sx={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          color: "var(--theme-color)",
                          mb: 1,
                        }}
                      >
                        Categories{" "}
                        <Box
                          component="span"
                          sx={{ fontWeight: 400, textTransform: "none" }}
                        >
                          (optional)
                        </Box>
                      </Typography>
                      <TaxonomyPicker
                        themeRanks={themeRanks}
                        propertyTypeRanks={propertyTypeRanks}
                        communityRanks={communityRanks}
                        onPickThemeAtRank={handlePickThemeAtRank}
                        onPickPropertyTypeAtRank={handlePickPropertyTypeAtRank}
                        onPickCommunityAtRank={handlePickCommunityAtRank}
                      />
                    </Box>
                  )}
                  {fileFieldsList.map((field, fileFieldIdx) => (
                    <Box
                      key={field.field_id}
                      sx={{ marginTop: "10px", marginBottom: "10px" }}
                    >
                      {/* In editMode, render the contribution's existing files
                          inline above the upload button, so the create and edit
                          panels share the same shape. Existing files attach to
                          the first file field (parity with the prior
                          EditStoryComponent.getFieldIdForFiles behavior). */}
                      {editMode && fileFieldIdx === 0 && (
                        <>
                          {(props.existingCarouselFiles?.length || 0) > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "var(--theme-color)",
                                  mb: 1,
                                }}
                              >
                                Images ({props.existingCarouselFiles.length})
                              </Typography>
                              {props.existingCarouselFiles.map((file, idx) => {
                                const fileName = (() => {
                                  try {
                                    return new URL(file?.filepath)?.pathname
                                      ?.split("uploads/")
                                      ?.pop();
                                  } catch (e) {
                                    return file?.name || file?.filename || "file";
                                  }
                                })();
                                const preview =
                                  file.preview ||
                                  (file.filepath || "./file.png");
                                return (
                                  <Box
                                    key={`existing-img-${file.id ?? idx}`}
                                    sx={{ mb: 1.5 }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1.5,
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={preview}
                                          alt={file.name || file.filename || fileName}
                                          sx={{ width: 42, height: 42, objectFit: "cover" }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: "14px",
                                            fontWeight: "400",
                                            color: "var(--theme-color)",
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            wordBreak: "break-word",
                                            maxWidth: "250px",
                                          }}
                                        >
                                          {file.name || file.filename || fileName}
                                        </Typography>
                                      </Box>
                                      <Button
                                        onClick={() => props.onExistingFileDelete?.(file)}
                                        sx={{
                                          background: "var(--theme-btn-clr)",
                                          color: "var(--theme-danger-clr)",
                                          textTransform: "none",
                                          fontSize: "14px",
                                          minWidth: "auto",
                                          px: 2,
                                          borderRadius: "2px",
                                          border: "0",
                                          fontWeight: "400",
                                        }}
                                      >
                                        Delete
                                      </Button>
                                    </Box>
                                    {file.id && props.onExistingFileCaptionSave && (
                                      <ExistingFileCaptionField
                                        fileId={file.id}
                                        initialCaption={file.caption || ""}
                                        onSave={props.onExistingFileCaptionSave}
                                      />
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                          {(props.existingNonImageFiles?.length || 0) > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: "700",
                                  color: "var(--theme-color)",
                                  mb: 1,
                                }}
                              >
                                Files ({props.existingNonImageFiles.length})
                              </Typography>
                              {props.existingNonImageFiles.map((file, idx) => (
                                <Box
                                  key={`existing-file-${file.id ?? idx}`}
                                  sx={{ mb: 1.5 }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={file.preview || (file.name ? getFileIcon(file.name) : "./file.png")}
                                        alt={file.name || file.filename}
                                        sx={{ width: 42, height: 42, objectFit: "cover" }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: "14px",
                                          fontWeight: "400",
                                          color: "var(--theme-color)",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          wordBreak: "break-word",
                                          maxWidth: "250px",
                                        }}
                                      >
                                        {file.name || file.filename}
                                      </Typography>
                                    </Box>
                                    <Button
                                      onClick={() => props.onExistingFileDelete?.(file)}
                                      sx={{
                                        background: "var(--theme-btn-clr)",
                                        color: "var(--theme-danger-clr)",
                                        textTransform: "none",
                                        fontSize: "14px",
                                        minWidth: "auto",
                                        px: 2,
                                        borderRadius: "2px",
                                        border: "0",
                                        fontWeight: "400",
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </Box>
                                  {file.id && props.onExistingFileCaptionSave && (
                                    <ExistingFileCaptionField
                                      fileId={file.id}
                                      initialCaption={file.caption || ""}
                                      onSave={props.onExistingFileCaptionSave}
                                    />
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </>
                      )}
                      <input
                        ref={(element) => {
                          legacyFileInputRefs.current[field.field_id] = element;
                        }}
                        accept="image/*,audio/mp3,audio/*"
                        id={`file-upload-${field.field_id}`}
                        multiple
                        type="file"
                        aria-label={`${field.field_name || "Attachments"} files`}
                        style={{ display: "none" }}
                        onChange={(e) => handleFileChange(e, field.field_id)}
                      />
                      <Button
                        type="button"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => legacyFileInputRefs.current[field.field_id]?.click()}
                        sx={{
                          minHeight: "44px",
                          backgroundColor: "var(--theme-color) !important",
                          color: "var(--theme-btn-clr) !important",
                          fontWeight: "900 !important",
                          textTransform: "capitalize !important",
                          lineHeight: "unset",
                        }}
                      >
                        Upload File
                      </Button>
                      <Stack direction="column" spacing={1} marginTop={2}>
                        {files[field.field_id]?.map((entry, index) => {
                          const file = entry.file != null ? entry.file : entry;
                          const caption =
                            entry.caption != null ? entry.caption : "";

                          const preview =
                            file.preview ||
                            (file.type?.startsWith("image/")
                              ? URL.createObjectURL(file)
                              : file.link
                                ? getFileIcon(file.name)
                                : file.name
                                  ? getFileIcon(file.name)
                                  : "./file.png");

                          return (
                            <Stack
                              key={`file-${field.field_id}-${file.name}-${index}`}
                              spacing={0.75}
                              sx={{ mb: 0.5 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={preview}
                                    alt={file.name || file.filename}
                                    sx={{
                                      width: 42,
                                      height: 42,
                                      objectFit: "cover",
                                    }}
                                  />

                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: "400",
                                      color: "var(--theme-color)",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      wordBreak: "break-word",
                                      maxWidth: "250px",
                                    }}
                                  >
                                    {file.name || file.filename}
                                  </Typography>
                                </Box>

                                <Button
                                  onClick={() =>
                                    handleRemoveFile(field.field_id, file.name)
                                  }
                                  sx={{
                                    background: "var(--theme-btn-clr)",
                                    color: "var(--theme-danger-clr)",
                                    textTransform: "none",
                                    fontSize: "14px",
                                    minWidth: "auto",
                                    px: 2,
                                    borderRadius: "2px",
                                    border: "0",
                                    fontWeight: "400",
                                    "&:hover": {
                                      backgroundColor:
                                        "var(--theme-btn-clr) !important",
                                      boxShadow: "none",
                                    },
                                  }}
                                >
                                  Delete
                                </Button>
                              </Box>
                              <TextField
                                size="small"
                                fullWidth
                                label="Caption (optional)"
                                value={caption}
                                onChange={(e) =>
                                  handleFileCaptionChange(
                                    field.field_id,
                                    index,
                                    e.target.value,
                                  )
                                }
                                sx={{
                                  "& .MuiInputLabel-root": {
                                    color: "var(--theme-color)",
                                  },
                                  "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                      borderColor: "var(--theme-color)",
                                    },
                                  },
                                }}
                              />
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                  {additionalFieldsList.length > 0 && (
                    <Stack spacing={1.5} sx={{ mt: 1.5, pb: 1 }}>
                          {additionalFieldsList.map((field) => {
                            const value = readValue(field.field_id);
                            const t = String(field.field_type || "").toLowerCase();
                            // Render by the field_type returned from
                            // FormHandler.php (canonical list in FormDetails.js
                            // FIELD_TYPES). `address`, `header`, `file`, and
                            // `select` never reach this list — they're filtered
                            // out by EXCLUDED_FROM_ADDITIONAL_TYPES upstream.
                            //
                            // `text` and `textarea` both render as multiline
                            // boxes matching the primary Story Of Place input.
                            // In practice every project's prose-y extras (e.g.
                            // Occupant, Artifacts, Notes) are stored as `text`
                            // even though they hold long-form content, so a
                            // single-line input is too cramped to be useful.
                            if (t === "number") {
                              return (
                                <TextField
                                  key={field.field_id}
                                  fullWidth
                                  type="number"
                                  label={field.field_name}
                                  value={value}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      field.field_id,
                                      e.target.value,
                                    )
                                  }
                                  sx={fieldSx}
                                />
                              );
                            }
                            if (t === "date") {
                              return (
                                <TextField
                                  key={field.field_id}
                                  fullWidth
                                  type="date"
                                  label={field.field_name}
                                  value={value}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      field.field_id,
                                      e.target.value,
                                    )
                                  }
                                  // shrink keeps the floating label out of the
                                  // way of the date placeholder ("mm/dd/yyyy")
                                  InputLabelProps={{ shrink: true }}
                                  sx={fieldSx}
                                />
                              );
                            }
                            // text / textarea / unknown → multiline (Story Of Place styling)
                            return (
                              <TextField
                                key={field.field_id}
                                fullWidth
                                multiline
                                minRows={4}
                                maxRows={20}
                                label={field.field_name}
                                value={value}
                                onChange={(e) =>
                                  handleFieldChange(
                                    field.field_id,
                                    e.target.value,
                                  )
                                }
                                sx={fieldSx}
                              />
                            );
                          })}
                    </Stack>
                  )}
                </>
              );
            })()}
          {!editMode && (!user || isExhibitionMode) && (
            <>
              <br />

              <Typography>
                <strong>Please fill in your information below.</strong>
              </Typography>
              <br />
              <TextField
                label="First Name"
                type="text"
                fullWidth
                name="contFirstName"
                value={contFirstName}
                onChange={(e) => setContFirstName(e.target.value)}
                className="form-input"
                required
                error={Boolean(error.contFirstName)}
                helperText={error.contFirstName}
                sx={{
                  marginTop: "10px",
                  marginBottom: "10px",
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
              <TextField
                label="Last Name"
                type="text"
                name="contLastName"
                fullWidth
                value={contLastName}
                onChange={(e) => setContLastName(e.target.value)}
                className="form-input"
                required
                error={Boolean(error.contLastName)}
                helperText={error.contLastName}
                sx={{
                  marginTop: "10px",
                  marginBottom: "10px",
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
              <TextField
                label="Email"
                type="email"
                name="contEmail"
                value={contEmail}
                fullWidth
                onChange={(e) => setContEmail(e.target.value)}
                className="form-input"
                required
                error={Boolean(error.email)}
                helperText={error.email || error.contEmail}
                sx={{
                  marginTop: "10px",
                  marginBottom: "10px",
                  "& .MuiInputLabel-root": { color: "var(--theme-color)" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "var(--theme-color)",
                  },
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--theme-color)",
                    },
                  },
                }}
              />
            </>
          )}

          {/*<label htmlFor="raised-button-file">
                        <VisuallyHiddenInput
                            accept="image/*,audio/*"
                            id="raised-button-file"
                            multiple
                            type="file"
                            onChange={(e) => setFiles([...files, ...e.target.files])}
                        />
                        <Button variant="contained" component="span" startIcon={<CloudUploadIcon/>}>
                            Upload File
                        </Button>
                    </label>
                        <Box mt={2}>
                    {files.map((file, index) => (
                        <Typography key={index}>
                            {file.name}
                            <IconButton onClick={() => setFiles(files.filter((_, i) => i !== index))}>
                                <DeleteOutlineIcon/>
                            </IconButton>
                        </Typography>
                    ))}
                </Box>
                */}

          {!editMode && <SubmissionUploadProgress progress={progress} />}
          {!editMode && (
            <Button
              sx={{
                marginTop: "10px",
                marginBottom: "25px",
                minHeight: "44px",
                backgroundColor: "var(--theme-color) !important",
                color: "var(--theme-btn-clr) !important",
                fontWeight: "900 !important",
                textTransform: "capitalize !important",
                lineHeight: "unset",
              }}
              variant="contained"
              type="submit"
              disabled={schemaStatus !== "ready" || !selectedForm}
            >
              Submit
            </Button>
          )}
        </form>
        </div>
      </div>
      <ConfirmationDialog
        open={confirmationOpen}
        onClose={handleConfirmationClose}
        title={confirmationTitle}
        message={confirmationMessage}
        actions={[
          {
            key: "confirm",
            element: (
              <Button
                variant="contained"
                onClick={handleConfirmationClose}
                sx={{
                  backgroundColor: "var(--theme-color) !important",
                  color: "var(--theme-btn-clr) !important",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                OK
              </Button>
            ),
          },
        ]}
      />
    </>
  );
}

export default ContributionComp;
